import useSWR, { mutate, SWRConfiguration } from 'swr'
import {
    FeesParams,
    FiatCountryDisabledReason,
    GetFiatTransferFeesRequest,
    GetFiatTransferRequisiteValuesRequest,
    GetFiatTransferTransactionDetailsRequest,
    GetTransactionsParams,
    GetCardTransactionsParams,
    IbanInfoParams,
    KycRequestStatus,
    KycUserInfo,
    RatesBatchParams,
    RatesParams,
    SwapFeatures,
    VerificationLevel,
    GetLocalesParams,
    GetIssueFormParams,
    GetCardBalanceParams,
    GetAgreementsParams,
    GetSensitiveDetailsParams,
    GetThreeDSConfigParams,
    GetThreeDSQuestionsParams,
    Balance,
} from '@web/walter-lib/api'
import { GetFiatFlowConfigParams } from '@web/walter-lib/api/types/cms/fiatFlow/paramsTypes'
import {
    marketApi,
    walletApi,
    userApi,
    buyCryptoApi,
    cmsApi,
    authApi,
    featuresApi,
    regApi,
    ensApi,
    vcardsApi,
    fiatWithdrawalApi,
    cardsApi,
    hostApi,
    spotApi,
} from '~api'
import { restKeys } from '~api/swr/keys'
import { useAuthSwr } from '~api/swr/useAuthSwr'
import { checkRequiredKycData } from '~utils/verification/checkRequiredKycData'
import { ModeTypes } from '~components/SwapWidget/const'
import { getTraceConfig } from '~api/utils'

const DEFAULT_POLLING_INTERVAL = 1000 * 60 * 5

interface Props {
    shouldRequest?: boolean
    operationId?: string
}

/** cms api */
export const useCommonCMSConfig = () => useSWR(restKeys.getCommonCMSConfig, () => cmsApi.getConfig())

export const useReceiveFiatList = () => useSWR(restKeys.getReceiveFiatList, () => cmsApi.getReceiveFiatList())

export const useGetWalletBalance = () =>
    useAuthSwr(restKeys.walletBalances, async () => {
        const data = await walletApi.getWalletBalances()
        const balancesMap: Record<string, Balance> = {}

        data?.forEach(item => {
            balancesMap[item.currency] = item
        })

        return balancesMap
    })

export const useGetSpotBalance = () =>
    useAuthSwr(restKeys.spotBalances, async () => {
        const data = await spotApi.getSpotTradingBalances()
        const balancesMap: Record<string, Balance> = {}

        data?.forEach(item => {
            balancesMap[item.currency] = item
        })

        return balancesMap
    })

export const useGetReceiveFiatConfig = ({ shouldRequest, currency }: Props & GetFiatFlowConfigParams) =>
    useAuthSwr(shouldRequest ? `${restKeys.getReceiveFiatConfig}_${currency}` : null, () =>
        cmsApi.getReceiveFiatConfig({ currency })
    )

export const useGetSendFiatConfig = ({ shouldRequest, currency }: Props & GetFiatFlowConfigParams) =>
    useAuthSwr(shouldRequest ? `${restKeys.getSendFiatConfig}_${currency}` : null, () =>
        cmsApi.getSendFiatConfig({ currency })
    )

export const useSendFiatList = () => useSWR(restKeys.getSendFiatList, () => cmsApi.getSendFiatList())

export const useGetBoundAddressConfig = (walletId?: string) => {
    // the right way to deal with args using array like [key, arg] => ([key, arg]) => {...} doesn't work here
    // it just sends 'e' as an argument to ensApi
    // TODO try to find out why
    return useSWR(walletId ? `${restKeys.getBoundAddressConfig}-${walletId}` : null, () => {
        return ensApi.resolveWalletId({ wallet_id: walletId! })
    })
}

/** host api */
export const useLocales = ({ data }: { data: GetLocalesParams }) => {
    return useSWR({ key: restKeys.getLocales, ...data }, () => hostApi.getLocales(data))
}
export const useConfig = () => useSWR(restKeys.getConfig, () => hostApi.getConfig())

/** market */
export const useCurrencies = (props?: Props) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useSWR(shouldRequest ? restKeys.getCurrencies : null, () =>
        marketApi.getCurrencies({}, getTraceConfig(operationId))
    )
}

export const useSymbols = () => {
    const { data, isLoading: isCmsLoading } = useCommonCMSConfig()
    const { isNewRateService } = data?.features ?? {}

    const {
        data: rateData,
        isLoading: isSymbolsLoading,
        ...rest
    } = useSWR(isNewRateService ? null : restKeys.getSymbols, () => marketApi.getSymbols())

    return { isLoading: isCmsLoading || isSymbolsLoading, data: rateData, ...rest }
}

/** wallet */
export const useSwapCurrencies = () => {
    const { isFixedOTCSwapsAvailable } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(restKeys.getSwapCurrencies, () =>
        walletApi.getSwapCurrencies(isFixedOTCSwapsAvailable ? { product: 'otc' } : undefined)
    )
}

export const useSwapFeatures = () => {
    const { isOtcMarketDisabled, isFixedOTCSwapsAvailable } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(restKeys.getSwapFeatures, async (): Promise<SwapFeatures> => {
        const result = await walletApi.getSwapFeatures(isFixedOTCSwapsAvailable ? { product: 'otc' } : undefined)

        if (isOtcMarketDisabled) {
            return {
                // Temporary fix. See WAL-2786 for details
                features: result.features?.filter(item => item !== ModeTypes.market),
            }
        }

        return result
    })
}

export const useStakingUser = (props?: Props) => {
    const { shouldRequest = false } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getStakingUser : null, () => walletApi.getStakingUser())
}

export const useStakingCurrencies = () => {
    const { showStaking } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(showStaking ? restKeys.getStakingCurrencies : null, () => walletApi.getStakingCurrencies())
}

export const useTransferConvertConfig = () =>
    useAuthSwr(restKeys.getTransferConvertConfig, () => walletApi.getTransferConvertConfig())

export const useTransactionsList = (
    props?: Props & {
        params: GetTransactionsParams
        revalidateOnMount?: boolean
    }
) => {
    const { shouldRequest = false, revalidateOnMount, params = { limit: 30 } } = props ?? {}

    return useAuthSwr(
        shouldRequest ? { key: restKeys.getTransactionsHistory, ...params } : null,
        () => walletApi.getTransactionsHistory(params),
        { revalidateOnMount }
    )
}

export const useGetRates = (props: Props & { params: RatesParams; key: string }) => {
    const { shouldRequest = false, params, key } = props

    return useSWR(
        shouldRequest && params.base_currencies.length ? restKeys.getRates + key : null,
        () => walletApi.getRates(params),
        {
            revalidateOnMount: true,
            refreshInterval: 10 * 60 * 1000, // 10 min
        }
    )
}

export const useGetRatesBatch = (props: Props & { params: RatesBatchParams; key?: string }) => {
    const { shouldRequest = false, key, params } = props

    return useSWR(shouldRequest ? restKeys.getRates + key : null, () => walletApi.getRatesBatch(params), {
        refreshInterval: 3000, // 3 sec
    })
}

export const usePrepareDepositCrypto = (props?: Props) => {
    const { shouldRequest = false } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.prepareDepositCrypto : null, () => walletApi.prepareDepositCrypto())
}

export const useTickers = () => {
    return useSWR(restKeys.getTickers, () => marketApi.getTickers())
}

/** user */
export const useUserInfo = (props?: Props) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getUserInfo : null, () =>
        userApi.getUserInfo(null, getTraceConfig(operationId))
    )
}

export const useUserAuth = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getUserAuth : null, () => userApi.getUserInfo(), {
        revalidateOnMount: true,
        onSuccess: data => {
            mutate(restKeys.getUserInfo, data, { revalidate: true })
        },
    })
}

export const useReferralLinks = () => useAuthSwr(restKeys.getReferralLinks, () => userApi.getReferralLinks())

export const useIsPermalinkSet = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.isPermalinkSet : null, () => userApi.isPermalinkSet())
}

export const useGetReferralBalance = () =>
    useAuthSwr(restKeys.getReferralBalance, () => userApi.getReferralBalance(), { revalidateOnMount: true })

export const useAppSettings = () => useAuthSwr(restKeys.getUserAppSettings, () => userApi.getUserAppSettings())

export const useMigrationStatus = () => useAuthSwr(restKeys.getMigrationStatus, () => userApi.getMigrationStatus())

/** buyCrypto */
export const useProviders = (props?: Props) => {
    const { blockBuyProviderRequest } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(blockBuyProviderRequest ? null : restKeys.getProvider, () =>
        buyCryptoApi.getProvider({}, getTraceConfig(props?.operationId))
    )
}

/** auth api */
export const useTotps = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getTotps : null, () => authApi.getTotps())
}

export const useGetIDPsList = (props?: Props) => {
    const { shouldRequest = false } = props ?? {}

    return useSWR(shouldRequest ? restKeys.getIDPsList : null, () => authApi.getIDPsList({ platform: 'web' }))
}

export const useGetActiveSessions = (config: SWRConfiguration = {}) =>
    useSWR(restKeys.getActiveSessions, () => authApi.getActiveSessions(), config)

/** features api */
export const useAssetLimits = (props?: Props, config: SWRConfiguration = {}) =>
    useAuthSwr(restKeys.getAssetLimits, () => featuresApi.getAssetLimits(null, getTraceConfig(props?.operationId)), {
        refreshInterval: result => {
            const needVerification = result?.fiat_country?.reason === FiatCountryDisabledReason.VERIFICATION_REQUIRED
            if (needVerification) {
                return DEFAULT_POLLING_INTERVAL
            }
            return 0
        },
        ...config,
    })

/** reg api */
export const useVerificationLevels = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getVerificationLevels : null, () => regApi.getVerificationLevels())
}

export const useAccountUserInfo = (props?: Props, config: SWRConfiguration = {}) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useAuthSwr(
        shouldRequest ? restKeys.getAccountUserInfo : null,
        () => regApi.getAccountUserInfo(null, getTraceConfig(operationId)),
        {
            refreshInterval: result => {
                const needVerification = result?.verification_level === VerificationLevel.STARTER_NONE
                if (needVerification) {
                    return DEFAULT_POLLING_INTERVAL
                }
                return 0
            },
            ...config,
        }
    )
}

export const useKycUserInfo = (props?: Props, config?: SWRConfiguration) =>
    useAuthSwr(restKeys.getKycUserInfo, () => regApi.getKycUserInfo(null, getTraceConfig(props?.operationId)), {
        ...(config ?? {}),
        refreshInterval: (result: KycUserInfo) => {
            const kycVerified = checkRequiredKycData(result)

            if (!kycVerified) {
                return DEFAULT_POLLING_INTERVAL
            }

            return 0
        },
    })

export const useKycRequestStatus = (config: SWRConfiguration<KycRequestStatus | undefined> = {}) =>
    useAuthSwr(restKeys.getKycRequestStatus, () => regApi.getKycRequestStatus(), config)

/** virtual cards api */
export const useVcardsBalances = (props?: Props) => {
    const { shouldRequest = false } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getVcardsBalances : null, () => vcardsApi.getVcardsBalances())
}

/** Iban api */
export const useGetIban = (props?: Props) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getIban : null, () =>
        walletApi.getIban(null, getTraceConfig(operationId))
    )
}

export const useIbanStatuses = (props?: Props) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getIbanStatuses : null, () =>
        walletApi.getIbanStatuses(null, getTraceConfig(operationId))
    )
}

export const useIbanInfo = (params: IbanInfoParams, props?: Props) =>
    useAuthSwr(restKeys.getIbanInfo, () => walletApi.getIbanInfo(params, getTraceConfig(props?.operationId)))

export const useIbanCurrencies = (props?: Props) => {
    const { shouldRequest = true, operationId } = props ?? {}

    return useSWR(shouldRequest ? restKeys.getIbanCurrencies : null, () =>
        walletApi.getIbanCurrencies(null, getTraceConfig(operationId))
    )
}

export const useGetFiatWithdrawalMethods = (props: Props & { currency: string }) => {
    const { shouldRequest = false, currency } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getFiatWithdrawalMethods : null, () =>
        fiatWithdrawalApi.getFiatWithdrawalMethods({ currency })
    )
}

/** Fiat Transfer api */
export const useFiatTransferMethods = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? restKeys.getFiatTransferMethods : null, () =>
        walletApi.getFiatTransferMethods(null)
    )
}

export const useFiatTransferRequisiteValues = (params: GetFiatTransferRequisiteValuesRequest, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(
        shouldRequest ? [restKeys.getFiatTransferRequisiteValues, params] : null,
        () => walletApi.getFiatTransferRequisiteValues(params),
        {
            revalidateIfStale: true,
        }
    )
}

export const useFiatTransferFees = (params: GetFiatTransferFeesRequest, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(
        shouldRequest ? [restKeys.getFiatTransferFees, params] : null,
        () => walletApi.getFiatTransferFees(params),
        {
            revalidateIfStale: true,
        }
    )
}

export const useFiatTransferTransactionDetails = (params: GetFiatTransferTransactionDetailsRequest, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? [restKeys.getFiatTransferTransactionDetails, params.tx_id] : null, () =>
        walletApi.getFiatTransferTransactionDetails(params)
    )
}

export const useEstimateWithdrawalFees = ({ params, key }: { params: FeesParams; key: string }) =>
    useAuthSwr(
        params?.length ? [restKeys.estimateWithdrawalFees, key, params] : null,
        () => walletApi.estimateWithdrawalFees(params),
        {
            refreshInterval: DEFAULT_POLLING_INTERVAL,
        }
    )

export const useGetHomeButtonsConfig = () =>
    useAuthSwr(restKeys.getHomeButtonsConfig, () => cmsApi.getHomeButtonsConfig(), {
        refreshInterval: DEFAULT_POLLING_INTERVAL,
    })

/** Cards api */

export const useProducts = () => useAuthSwr(restKeys.getProducts, () => cardsApi.getProducts())

export const useCards = () => {
    const { showCards } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(showCards ? restKeys.getCards : null, () => cardsApi.getCards())
}
export const useIssueForm = (params: GetIssueFormParams) =>
    useAuthSwr([restKeys.getIssueForm, params.id], () => cardsApi.getIssueForm({ id: params.id }))
export const useCardBalance = (params: GetCardBalanceParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(
        shouldRequest ? [restKeys.cardsBalance, params.id] : null,
        () => cardsApi.cardsBalance({ id: params.id }),
        {
            refreshInterval: 1000 * 10,
        }
    )
}

export const useGetAgreements = (params: GetAgreementsParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? [restKeys.getAgreements, params.productId, params.agreementId] : null, () =>
        cardsApi.getAgreements(params)
    )
}

export const useCardTransactionsList = (
    props?: Props & {
        params: GetCardTransactionsParams
        revalidateOnMount?: boolean
    }
) => {
    const { shouldRequest = false, revalidateOnMount, params } = props ?? {}

    return useAuthSwr(
        shouldRequest ? { key: restKeys.getTransactionsCardsHistory, ...params } : null,
        () => cardsApi.getTransactions(params || { id: '' }),
        { revalidateOnMount }
    )
}

export const useGetSensitiveData = (params: GetSensitiveDetailsParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? [restKeys.getSensitiveDetails, params.id] : null, () =>
        cardsApi.getSensitiveDetails(params)
    )
}

export const useGetThreeDSChallengesData = (_?: void, props?: Props) => {
    const { shouldRequest = true } = props ?? {}
    const { showCards } = useCommonCMSConfig().data?.features ?? {}

    return useAuthSwr(
        shouldRequest && showCards ? restKeys.getThreeDSChallenges : null,
        () => cardsApi.getThreeDSChallenges(),
        {
            refreshInterval: 1000 * 10,
        }
    )
}

export const useGetThreeDSConfigData = (params: GetThreeDSConfigParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? { key: restKeys.getThreeDSConfig, ...params } : null, () =>
        cardsApi.getThreeDSConfig({ id: params.id })
    )
}

export const useGetThreeDSMethods = (params: GetThreeDSConfigParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? { key: restKeys.getThreeDSMethods, ...params } : null, () =>
        cardsApi.getThreeDSMethods({ id: params.id })
    )
}

export const useGetThreeDSQuestionsData = (params: GetThreeDSQuestionsParams, props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(shouldRequest ? { key: restKeys.getThreeDSQuestions, ...params } : null, () =>
        cardsApi.getThreeDSQuestions({ id: params.id })
    )
}

export const useGetManualKycLevelsConfig = (props?: Props) => {
    const { shouldRequest = true } = props ?? {}

    return useAuthSwr(
        shouldRequest ? restKeys.getManualKycLevelsConfig : null,
        () => cmsApi.getManualKycLevelsConfig(),
        {
            refreshInterval: DEFAULT_POLLING_INTERVAL,
        }
    )
}
